import {
  type ReactNode,
  createContext,
  useRef,
  useContext,
  useMemo,
} from 'react'
import { useStore } from 'zustand'
import {
  createInspectionDetailStore,
  InspectionDetailComponents,
  InspectionDetailContext,
  InspectionDetailRPC,
  InspectionDetailState,
  InspectionDetailStore,
} from './factory'
import {
  ImageInferenceAttribute,
  RetrieveInferenceJSONResponseBody,
  TemplateInput,
} from 'bff'
import { InputType, inputTypeFiles } from 'ai-constants'
import { HydratedInspectionDetailResponseBody } from 'bff'
import { t } from 'i11n'
import {
  IImageDetailMinimized,
  InspectionFile,
} from '@detail/components/InspectionDetail/types'

type CompanyVariablesMap = Record<string, unknown>

export type InspectionDetailStoreApi = {
  store: ReturnType<typeof createInspectionDetailStore>
  components?: InspectionDetailComponents
  rpc?: InspectionDetailRPC
  companyVariables?: CompanyVariablesMap
}

export const InspectionDetailStoreContext = createContext<
  InspectionDetailStoreApi | undefined
>(undefined)

export interface InspectionDetailStoreProviderProps {
  children: ReactNode
  store: {
    inspection: HydratedInspectionDetailResponseBody & {
      tracking?: InspectionDetailState['tracking']
    }
    authUser: InspectionDetailState['authUser']
    focusedFileId?: string
  }
  components?: InspectionDetailComponents
  context: InspectionDetailContext
  rpc: Partial<InspectionDetailRPC>
  companyVariables?: CompanyVariablesMap
  platform: 'office' | 'dashboard'
}

const buildImageIdentifier = (
  image: IImageDetailMinimized,
  groupImages: IImageDetailMinimized[],
) => {
  const imagesSortedByUploadDate = Array.from(groupImages).sort(
    (a, b) =>
      new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime(),
  )

  const latestImage =
    imagesSortedByUploadDate[imagesSortedByUploadDate.length - 1]

  if (latestImage._id === image._id) return image.templateImageIdentifier

  return image._id
}

const sectionsOrder = ['identity', 'products', 'custom', 'complete'] as const

export const aggregateInspectionFiles = (
  inspection: Pick<
    HydratedInspectionDetailResponseBody,
    'sections' | 'documents'
  >,
): InspectionFile[] => {
  const reduceSectionItem = (
    item: ISectionValue<
      Array<
        TemplateInput & {
          label: string
          src: string
        }
      >
    >,
  ): InspectionFile[] => {
    return [
      ...item.fields
        .filter((field) => inputTypeFiles.includes(field.type) && !!field.value)
        .map((field) => {
          //input with multiple instances will have an instanceId, otherwise use the template identifier
          const identifier = field.instanceId || field.identifier

          return {
            assetType: 'input',
            matches: field.matches || [],
            type: field.type,
            src: field.src,
            identifier,
            previewURL: field.previewURL,
            value: field.value,
            label: field.label,
            inferences: field.inferences,
            validations: field.validations,
            edges: field.edges,
            refreshToken: field.refreshToken,
            spec: {
              identifier: field.identifier,
            },
            instanceId: field.instanceId,
            instance: {
              _id: identifier,
            },
          }
        }),
      ...Object.values(item.images).flatMap((group) =>
        group.images.map((image): InspectionFile => {
          return {
            assetType: 'image',
            type: 'image',
            src: image.src,
            identifier: buildImageIdentifier(image, group.images),
            label: group.label || t(image.label),
            value: image.src,
            image,
            matches: image.matches || [],
            inferences: image.inferences,
            validations: image._validations,
            edges: image.edges,
            refreshToken: image.refreshToken,
            spec: {
              identifier: image.templateImageIdentifier,
            },
            instanceId: image._id,
            instance: {
              _id: image._id,
            },
          }
        }),
      ),
    ]
  }

  return [
    ...(inspection.documents || [])
      .filter((document) => !!document.value)
      .map(
        (document): InspectionFile => ({
          assetType: 'document',
          type: 'document',
          document,
          identifier: document.identifier,
          label: document.name,
          src: document.src,
          value: document.value!,
          previewURL: document.previewURL,
          inferences: document.inferences,
          validations: document.validations,
          matches: document.matches || [],
          edges: document.edges,
          spec: {
            identifier: document.identifier,
          },
          instance: {
            _id: document.identifier,
          },
        }),
      ),
    ...Object.entries(inspection.sections)
      .sort(([a], [b]) => sectionsOrder.indexOf(a) - sectionsOrder.indexOf(b))
      .flatMap(([section, data]: [string, ISectionValue | ISectionValue[]]) => {
        if (Array.isArray(data))
          return data.flatMap((item) => reduceSectionItem(item))

        return reduceSectionItem(data)
      }),
  ]
}

type InspectionDetailProviderCommonProps = Omit<
  InspectionDetailStoreProviderProps,
  'store' | 'rpc'
> &
  InspectionDetailStoreProviderProps['store']

export type UnionInspectionDetailStoreProviderProps =
  | (InspectionDetailProviderCommonProps & {
      context: 'web'
    } & Pick<InspectionDetailStoreProviderProps, 'rpc'>)
  | (InspectionDetailProviderCommonProps & {
      context: 'pdf'
    })

export const InspectionDetailStoreProvider = ({
  children,
  inspection,
  authUser,
  components,
  focusedFileId,
  companyVariables,
  platform,
  ...props
}: UnionInspectionDetailStoreProviderProps) => {
  const inspectionFiles = useMemo(
    () => aggregateInspectionFiles(inspection),
    [],
  )

  const storeRef = useRef<InspectionDetailStoreApi>(null)

  if (!storeRef.current) {
    //@ts-ignore
    storeRef.current = createInspectionDetailStore({
      ...inspection,
      tracking: inspection.tracking || [],
      inspectionFiles,
      authUser,
      context: props.context,
      focusedFileId,
      platform,
    })
  }

  return (
    <InspectionDetailStoreContext.Provider
      value={{
        store: storeRef.current!,
        components,
        ...(props.context === 'web'
          ? {
              rpc: props.rpc,
            }
          : {}),
        companyVariables,
      }}
    >
      {children}
    </InspectionDetailStoreContext.Provider>
  )
}

export const useInspectionDetailComponents = () => {
  const inspectionDetailStoreContext = useContext(InspectionDetailStoreContext)

  if (!inspectionDetailStoreContext)
    throw new Error(
      `useInspectionDetailComponents must be used within InspectionDetailStoreProvider`,
    )

  return inspectionDetailStoreContext!.components
}

export const useInspectionDetailRPC = () => {
  const inspectionDetailStoreContext = useContext(InspectionDetailStoreContext)

  if (!inspectionDetailStoreContext)
    throw new Error(
      `useInspectionDetailRPC must be used within InspectionDetailStoreProvider`,
    )

  return inspectionDetailStoreContext.rpc!
}

export const useInspectionDetailCompanyVariables = () => {
  const inspectionDetailStoreContext = useContext(InspectionDetailStoreContext)

  if (!inspectionDetailStoreContext)
    throw new Error(
      `useInspectionDetailCompanyVariables must be used within InspectionDetailStoreProvider`,
    )

  return inspectionDetailStoreContext.companyVariables!
}

export const NOT_FOUND_INDEX = -1

export const findFocusedFile = <T extends 'findIndex' | 'find'>(
  method: T,
  files: InspectionFile[],
  focusedFileId?: string | null,
): T extends 'findIndex' ? number | undefined : InspectionFile | undefined => {
  if (!focusedFileId) return undefined

  if (method === 'findIndex') {
    const fineGrainedIndex = files.findIndex(
      (file) => file.instance._id === focusedFileId,
    )

    //@ts-ignore
    if (fineGrainedIndex !== NOT_FOUND_INDEX) return fineGrainedIndex

    const globalScopeIndex = files.findIndex(
      (file) => file.identifier === focusedFileId,
    )

    //@ts-ignore
    return globalScopeIndex
  }

  //@ts-ignore
  return (
    files.find((file) => file.instance._id === focusedFileId) ||
    files.find((file) => file.identifier === focusedFileId)
  )
}

export const useInspectionDetailStore = <T,>(
  selector: (store: InspectionDetailStore) => T,
  unsafe: boolean = false,
  equalityFn?: (a: T, b: T) => boolean,
): T => {
  const inspectionDetailStoreContext = useContext(InspectionDetailStoreContext)

  if (!inspectionDetailStoreContext && !unsafe)
    throw new Error(
      `useInspectionDetailStore must be used within InspectionDetailStoreProvider`,
    )

  return useStore(inspectionDetailStoreContext!.store, selector, equalityFn)
}
